import { Path } from '@vimeo/iris/components';
import { Header, Link, Paragraph } from '@vimeo/iris/typography';

import CodeBlock from 'src/_components/CodeBlock';
import GuideTable from 'src/_components/GuideTable';
import GuideWrapper from 'src/_components/GuideWrapper';
import Meta from 'src/_components/Meta';
import StyledNotice from 'src/_components/StyledNotice';

export default function RateLimit() {
  return (
    <>
      <Meta
        title="Working with Rate Limits"
        description="Understand how rate limiting works on the Vimeo API, and learn techniques for maximizing your access."
      />
      <GuideWrapper
        breadcrumbs={
          <Path>
            <Path.Link href={'/'}>Home</Path.Link>
            <Path.Link href={'/api/guides/start'}>API</Path.Link>
            <Path.Current>Working with Rate Limits</Path.Current>
          </Path>
        }
        nav={{ page: 'rate-limiting' }}
      >
        <Header>Working with Rate Limits</Header>
        <Paragraph size="2">
          The Vimeo API observes <em>rate limits</em>, which means that we impose a maximum number
          of requests that the end users of your app can make per minute. If any of your users
          exceeds this limit, we put your app in cooldown for them. They must wait until their quota
          resets before they can make new requests through your app.
        </Paragraph>

        <Paragraph size="2">
          This guide explains the ins and outs of rate limiting on the Vimeo API.
        </Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> This information is current for our latest API release, version
          3.4. For complete details on 3.4, see our{' '}
          <Link variant="minimal" href="/api/changelog">
            Changelog
          </Link>
          .
        </StyledNotice>

        <Header size="2" id="requests-per-minute">
          How many requests do I get per minute?
        </Header>
        <Paragraph size="2">
          Your end user's Vimeo membership level determines how many API requests they can make from
          your app per minute. See{' '}
          <Link variant="minimal" href="#table-1">
            Table 1
          </Link>{' '}
          for the details.
        </Paragraph>

        <GuideTable
          tableNumber={1}
          description="API requests per minute according to your end user's Vimeo membership"
          headers={["Your end user's membership", 'API requests per minute (per user*)']}
          rows={[
            ['Vimeo Free', 25],
            ['Vimeo Starter', 125],
            ['Vimeo Standard', 250],
            ['Vimeo Advanced', 750],
            ['Vimeo Enterprise', 2500],
          ]}
        />

        <Paragraph size="4">* Assuming an authenticated access token.</Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> If you use field filtering to specify exactly which data fields to
          return, we effectively double your quota from{' '}
          <Link variant="minimal" href="#table-1">
            Table 1
          </Link>{' '}
          . For more information, see{' '}
          <Link variant="minimal" href="#use-field-filtering">
            Use field filtering
          </Link>{' '}
          below.
        </StyledNotice>

        <Paragraph size="2">
          For the purposes of rate limiting, the type of{' '}
          <Link variant="minimal" href="/api/authentication">
            authorization token
          </Link>{' '}
          that we receive determines who the end user is. With an unauthenticated token, the end
          user is the owner of the app. We count all the requests as coming from this person, even
          if multiple users are generating them. The cooldown for the app kicks in if the total
          number of API requests exceeds the value in{' '}
          <Link variant="minimal" href="#table-1">
            Table 1
          </Link>{' '}
          that corresponds to the app owner's membership level.
        </Paragraph>

        <Paragraph size="2">
          With an authenticated token, the end user is the person identified by the token. We
          consider each token individually, so that every authenticated end user has their own
          independent quota, which doesn't impact any other user's quota.
        </Paragraph>

        <Header size="2" id="legacy-membership">
          What if I have a legacy membership?
        </Header>

        <Paragraph size="2">
          If your end user is rocking one of our older Vimeo subscription plans, see{' '}
          <Link variant="minimal" href="#table-2">
            Table 2
          </Link>{' '}
          for the maximum number of API requests per minute that they get. If you make use of{' '}
          <Link variant="minimal" href="#use-field-filtering">
            field filtering
          </Link>
          , go ahead and double that number. For each of your users to have their own pool of
          requests, make sure that they're all presenting authenticated access tokens.
        </Paragraph>

        <GuideTable
          tableNumber={2}
          description="API requests per minute according to your end user's legacy Vimeo membership"
          headers={["Your end user's membership", 'API requests per minute (per user*)']}
          rows={[
            ['Vimeo Basic', 25],
            ['Vimeo Plus', 125],
            ['Vimeo PRO', 250],
            ['Vimeo Business', 500],
            ['Vimeo Premium', 750],
          ]}
        />

        <Paragraph size="4">* Assuming an authenticated access token.</Paragraph>

        <Header size="2" id="quota-usage">
          How much of my quota have I used so far?
        </Header>

        <Paragraph size="2">
          Each API request comes back with three headers related specifically to rate limiting:{' '}
          <code>
            <b>X-RateLimit-Limit</b>
          </code>
          ,{' '}
          <code>
            <b>X-RateLimit-Remaining</b>
          </code>
          , and{' '}
          <code>
            <b>X-RateLimit-Reset</b>
          </code>{' '}
          (see{' '}
          <Link variant="minimal" href="#table-3">
            Table 3
          </Link>
          ). Together, these headers give you a complete snapshot of the requester's API usage
          level.
        </Paragraph>

        <GuideTable
          tableNumber={3}
          description="API response headers for rate limiting"
          headers={['Header', 'Returns']}
          rows={[
            [
              <code key="strong-1">
                <b>X-RateLimit-Limit</b>
              </code>,
              <span key="span-1">
                The maximum number of API responses that the requester can make through your app in
                any given 60-second period.<sup>*</sup>
              </span>,
            ],
            [
              <code key="strong-2">
                <b>X-RateLimit-Remaining</b>
              </code>,
              <span key="span-2">
                The remaining number of API responses that the requester can make through your app
                in the current 60-second period.<sup>*</sup>
              </span>,
            ],
            [
              <code key="strong-3">
                <b>X-RateLimit-Reset</b>
              </code>,
              'A datetime value indicating when the next 60-second period begins.',
            ],
          ]}
        />

        <Paragraph size="4">
          * The values of{' '}
          <code>
            <b>X-RateLimit-Limit</b>
          </code>{' '}
          and{' '}
          <code>
            <b>X-RateLimit-Remaining</b>
          </code>{' '}
          assume that you're using field filtering to double the normal request quota. If you aren't
          using field filtering, divide these values by 2. For more information, see{' '}
          <Link variant="minimal" href="#use-field-filtering">
            Use field filtering
          </Link>{' '}
          below.
        </Paragraph>

        <Header size="2" id="exceed-quota">
          What happens if I exceed my quota?
        </Header>
        <Paragraph size="2">
          If your end user blows past their rate limit, the API stops processing their requests
          through your app for the remainder of the current 60-second period. During this time, any
          request from your app for this end user results in HTTP status code 429 and error code
          9000.
        </Paragraph>

        <Paragraph size="2">
          For typical apps under typical conditions, hitting the rate limit is an infrequent sort of
          thing. If your end users are reaching it more often than that, be sure to refer them to{' '}
          <Link variant="minimal" href="https://vimeo.com/upgrade">
            vimeo.com/upgrade
          </Link>{' '}
          to level up their membership.
        </Paragraph>

        <Paragraph size="2">
          For cases where the quota is exceeded repeatedly, we might decide that blocking your app
          is the answer.{' '}
          <Link variant="minimal" href="https://vimeo.com/help/contact">
            Contact us
          </Link>{' '}
          if this happens to you to clear up any misconceptions.
        </Paragraph>

        <Header size="2" id="avoid-rate-limiting">
          How can I avoid rate limiting?
        </Header>
        <Paragraph size="2">
          Here are some suggestions for keeping your apps on the right side of the rate-limit line.
        </Paragraph>

        <Header size="3" id="use-field-filtering">
          Use field filtering
        </Header>
        <Paragraph size="2">
          <em>Field filtering</em> is a technique where you request only specific fields of a JSON
          representation instead of the entire representation. We like field filtering so much that
          we effectively double the normal rate-limiting quota on any API request that uses it.
          Refer to{' '}
          <Link variant="minimal" href="#table-4">
            Table 4
          </Link>{' '}
          to see what we mean.
        </Paragraph>

        <GuideTable
          tableNumber={4}
          description="API requests per minute, with and without field filtering"
          headers={[
            "Your end user's membership",
            'API requests per minute (per user*), without field filtering',
            'Effective API requests per minute (per user*), with field filtering',
          ]}
          rows={[
            ['Vimeo Free', 25, 50],
            ['Vimeo Starter', 125, 250],
            ['Vimeo Standard', 250, 500],
            ['Vimeo Advanced', 750, 1500],
            ['Vimeo Enterprise', 2500, 5000],
          ]}
        />

        <Paragraph size="4">* Assuming an authenticated access token.</Paragraph>

        <Paragraph size="2">
          To do field filtering on a request, append the{' '}
          <code>
            <b>fields</b>
          </code>{' '}
          parameter to the endpoint in question, and then give a comma-separated list of the fields
          to return, like this:
        </Paragraph>

        <CodeBlock
          endpoint={{
            method: 'get',
            resource: '/me/videos/258684937',
            params: '?fields=name,description',
          }}
        />

        <Paragraph size="2">
          We explain this technique more thoroughly in our{' '}
          <Link variant="minimal" href="/api/common-formats#json-filter">
            Using Common Formats and Parameters
          </Link>{' '}
          guide.
        </Paragraph>

        <Header size="3" id="cache-api-responses">
          Cache API responses
        </Header>
        <Paragraph size="2">
          To minimize the number of requests that you make to the API, especially if you expect a
          high level of traffic, try calling the API once and creating a local cache for the
          response instead of making a new API call with each page load. You can populate the data
          on your page from the cache instead of from a redundant JSON representation.
        </Paragraph>

        <Header size="3" id="upgrade-membership">
          Upgrade your membership
        </Header>
        <Paragraph size="2">
          A higher membership level means more requests per minute. Learn more about our plans on
          our{' '}
          <Link variant="minimal" href="https://vimeo.com/upgrade">
            upgrade
          </Link>{' '}
          page.
        </Paragraph>

        <Header size="3" id="dont-scrape-us">
          Don't scrape our site
        </Header>
        <Paragraph size="2">
          <em>Web scraping</em> is the process of extracting site data directly from a rendered HTML
          page instead of through the API. If you're not making API requests, you're not subject to
          rate limits.
        </Paragraph>

        <Paragraph size="2">
          Just one thing. We don't permit web scraping on Vimeo. An excessive amount of
          scraping-like server activity grants its associated IP a one-way ticket to Bannedville.
          But once you see how finely tuned and intuitive our API is, you'll forget all about your
          scraper bot anyway.
        </Paragraph>
      </GuideWrapper>
    </>
  );
}
